/**
 * Sprint 5B — DashboardSectionState
 *
 * Reusable wrapper for any Dashboard section that consumes the
 * DASH-002 envelope:
 *
 *   { status: "ok"|"forbidden"|"failed"|"empty"|"stale"|"degraded"|"loading",
 *     data, error }
 *
 * Rules (Sprint 5B / DASH-005A):
 *  - forbidden NEVER renders as zero,
 *  - failed   NEVER renders green,
 *  - stale + degraded are visibly labeled,
 *  - loading is explicit (skeleton).
 *
 * Pass the children render-prop only the *ok* branch — non-ok statuses
 * render here so individual tiles cannot accidentally show data when
 * the backend said no.
 */
import React from 'react';
import { AlertTriangle, Lock, RefreshCw, Inbox, Clock } from 'lucide-react';

export function DashboardSectionState({
  status,
  data,
  error,
  generatedAt,
  stale,
  title,
  emptyHint,
  children,
  testIdPrefix = 'dash-section',
}) {
  // 1) Loading
  if (status === 'loading') {
    return (
      <div
        data-testid={`${testIdPrefix}-loading`}
        className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 animate-pulse"
      >
        <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
        <div className="h-3 w-28 bg-slate-100 dark:bg-slate-700/60 rounded mt-3" />
        <div className="h-16 mt-5 bg-slate-100 dark:bg-slate-700/40 rounded" />
      </div>
    );
  }

  // 2) Forbidden — explicit. Never zero / never green.
  if (status === 'forbidden') {
    return (
      <div
        data-testid={`${testIdPrefix}-forbidden`}
        className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-5"
      >
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <Lock className="w-4 h-4" />
          <span className="text-sm font-medium">{title || 'Restricted'}</span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          You don&apos;t have permission to view this section.
        </p>
      </div>
    );
  }

  // 3) Failed — explicit. Never zero / never green.
  if (status === 'failed' || status === 'degraded') {
    return (
      <div
        data-testid={`${testIdPrefix}-failed`}
        className="bg-rose-50 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900 p-5"
      >
        <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">{title || 'Section unavailable'}</span>
        </div>
        <p className="text-xs text-rose-600 dark:text-rose-300 mt-2 break-all">
          {error || 'This section could not be loaded.'}
        </p>
      </div>
    );
  }

  // 4) Empty — explicit empty-state, not zero data.
  if (status === 'empty') {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5"
      >
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <Inbox className="w-4 h-4" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {emptyHint || 'No data to show yet.'}
        </p>
      </div>
    );
  }

  // 5) OK — children render the actual tile. Stale/degraded label is
  // overlaid on top so the operator can tell the data may be old.
  return (
    <div data-testid={`${testIdPrefix}-ok`} className="relative">
      {(stale || status === 'stale') && (
        <div
          data-testid={`${testIdPrefix}-stale-flag`}
          className="absolute top-2 right-2 flex items-center gap-1 text-xs text-amber-700 bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300 px-2 py-0.5 rounded-full"
        >
          <Clock className="w-3 h-3" />
          Stale
        </div>
      )}
      {typeof children === 'function' ? children(data) : children}
      {generatedAt && (
        <div
          data-testid={`${testIdPrefix}-generated`}
          className="text-[10px] text-slate-400 mt-2"
        >
          <RefreshCw className="w-3 h-3 inline mr-1" />
          {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default DashboardSectionState;
